import { all, call, put, select } from 'redux-saga/effects';
import { getFormValues, arrayPush, registerField, change } from 'redux-form';
import { differenceInMinutes } from 'date-fns'

import { format, addYears, toDate, extendTo, toMinutes } from '@/utils/datetime';
import { apiClient } from '@/utils';
import * as actions from '@/store/actions';
import { throwSubmissionError } from '@/utils';
import { stringifyNestedParams, parseIntervalKey } from '@/utils/common';
import { DATE_QUANTITIES_PREFIX } from '@/containers/Booking/NewBooking/DateQuantities';
import { AnyAction } from 'redux';
import { CustomField, Price, Time } from '@/types';
import { AxiosResponse } from 'axios';
import { RootState } from 'MyTypes';
import { definitions } from '@/apitypes';
import { BookingUserQueueItemResponse } from '@/types';
import { ApplicationState } from '@/store';
import { flatten, omit, pickBy } from 'lodash';
import { getServiceQuantities } from '../reducers/newBooking';
import {
    buildFreeSpotsByFrom,
    getFreeSpotsByFrom,
    toInstantKey,
} from '../reducers/availableTimes';
import { getAppliedCodeIds } from '../reducers/calculatePrice';
import { selectAllQuantities, parseSliceIntervalKey } from '../reducers/dateQuantitiesForm';
import { isPriceValidForOccurrence } from '@/utils/priceValidity';

/**
 * Resolve quantities for a given booking date.
 *
 * Checks three sources in priority order:
 *  1. redux-form DateQuantities_ instances (non-group services)
 *  2. dateQuantitiesForm Redux slice (group/multi-resource services)
 *  3. Legacy state.booking.quantities fallback
 *
 * @param fromDate          ISO date string of the booking start time
 * @param forms             The entire redux-form state (state.form), or undefined
 * @param sliceQuantities   The dateQuantitiesForm slice quantities
 * @param legacyQuantities  Fallback quantities from state.booking.quantities
 * @param prices            The service's (union) price list — when supplied, quantities whose
 *                          PriceId is not valid for `fromDate` are dropped
 */
export function resolveQuantitiesForDate(
    fromDate: string,
    forms: Record<string, any> | undefined,
    sliceQuantities: Record<string, Record<number, number>>,
    legacyQuantities: any,
    prices?: Price[]
): Array<{ Quantity: number; PriceId: number }> {
    // The backend accepts an out-of-window PriceId on /bookings/repeat: it falls back to a raw
    // DB lookup and books it at that price row's real amount, while calculateprice silently
    // dropped the same line from the quoted total. This is the last line of defence.
    // A quantity with no PriceId is the no-prices case (getServiceQuantities) and must survive.
    const keepValid = (quantities: Array<{ Quantity: number; PriceId: number }>) =>
        prices
            ? quantities.filter(
                  (q) =>
                      q.PriceId == null ||
                      prices.some((p) => p.Id === q.PriceId && isPriceValidForOccurrence(p, fromDate))
              )
            : quantities;

    // 1. Try redux-form (non-group path)
    if (forms) {
        const target = new Date(fromDate).getTime();
        const formQty = keepValid(
            Object.values(
                pickBy(forms, (_value: any, key: string) => {
                    if (!fromDate || !key.includes(DATE_QUANTITIES_PREFIX)) return false;
                    const { From } = parseIntervalKey(key, DATE_QUANTITIES_PREFIX);
                    const t = new Date(From).getTime();
                    return !Number.isNaN(t) && t === target;
                })
            )
                .map((c: any) => c.values)
                .flat()
                .filter((c: any) => c && c.quantity > 0)
                .map((c: any) => ({ Quantity: c.quantity, PriceId: c.price }))
        );
        if (formQty.length > 0) return formQty;
    }

    // 2. Try dateQuantitiesForm slice (group path)
    const normalizedFrom = new Date(fromDate).toISOString();
    for (const [intervalKey, pricesMap] of Object.entries(sliceQuantities)) {
        const parsed = parseSliceIntervalKey(intervalKey);
        if (new Date(parsed.From).toISOString() === normalizedFrom) {
            const sliceQty = keepValid(
                Object.entries(pricesMap)
                    .filter(([, qty]) => qty > 0)
                    .map(([priceId, qty]) => ({ Quantity: qty, PriceId: Number(priceId) }))
            );
            if (sliceQty.length > 0) return sliceQty;
        }
    }

    // 3. Fallback to legacy quantities
    return keepValid(legacyQuantities || []);
}

const DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Union of the date-scoped price list and the "other days" superset, keyed by Id.
 * The primary row wins: for WeightedPrices companies it carries the weighted `Price`,
 * which the superset row would not.
 */
export function mergePricesById(primary: Price[] = [], superset: Price[] = []): Price[] {
    const byId = new Map<number, Price>();
    (superset || []).forEach((p) => byId.set(p.Id, p));
    (primary || []).forEach((p) => byId.set(p.Id, p));
    return Array.from(byId.values()).sort((a, b) => a.Price - b.Price);
}

export function* fetchServiceWithPrice(action: AnyAction) {
    const bookingForm: { [key in string]: any } = yield select(getFormValues('booking'));

    try {
        const dateParams = {
            ...(bookingForm?.From ? { PriceDate: format(bookingForm.From, DATE_FORMAT) } : {}),
            ...(bookingForm?.From ? { PriceTime: format(bookingForm.From, 'HH:mm') } : {}),
        };

        // The recurring flow books N dates against ONE service fetch, so the category list must be the
        // union over the whole series. `IncludePricesFromOtherDays` returns future-dated categories but
        // drops any whose To is the current day (ServiceExtensions.cs:270-272), hence the union.
        const [response, supersetResponse]: [AxiosResponse, AxiosResponse | null] = yield all([
            apiClient.get('/services', {
                params: {
                    Id: bookingForm.ServiceId,
                    IncludeBookingCustomFields: true,
                    IncludeCustomFieldValues: true,
                    IncludeCustomFields: true,
                    IncludePrices: true,
                    ...dateParams,
                },
            }),
            apiClient
                .get('/services', {
                    params: {
                        Id: bookingForm.ServiceId,
                        IncludePrices: true,
                        IncludePricesFromOtherDays: true,
                        ...dateParams,
                    },
                })
                .catch(() => null), // degrade to the date-scoped list; never block the booking flow
        ]);

        const primaryService = response.data.Results[0];
        const service = {
            ...primaryService,
            Prices: mergePricesById(
                primaryService?.Prices,
                supersetResponse?.data?.Results?.[0]?.Prices
            ),
        };

        yield put({
            type: actions.FETCH_SERVICE_WITH_PRICE.SUCCESS,
            payload: service,
        });

        yield put(change('booking', 'CustomFields', []));
        yield put(registerField('booking', 'CustomFields', 'FieldArray'));
        const sortedBookingCustomFields = [...service.BookingCustomFields].sort(
            (a: CustomField, b: CustomField) => (b.SortOrder ?? 0) - (a.SortOrder ?? 0)
        );
        yield all(
            sortedBookingCustomFields.map((field: CustomField) =>
                put(
                    arrayPush('booking', 'CustomFields', {
                        Id: field.Id,
                        Value: service.DefaultValue,
                        meta: field,
                    })
                )
            )
        );
    } catch (error) {
        yield put({
            type: actions.FETCH_SERVICE_WITH_PRICE.FAILURE,
            payload: error,
        });
    }
}

export function* bookingFetchAvailableTimes({ payload }: AnyAction) {
    const { From, To, Resources, ServiceId, Duration } = payload;

    const params: any = {
        From: format(From, DATE_FORMAT),
        To: format(To, DATE_FORMAT),
        ...(Duration ? { Duration } : {})
    };

    // When allResources is selected we want to show all available times,
    // but keep other resources that have been selected
    if (Resources && Array.isArray(Resources)) {
        const filteredResources = Resources
            .filter((resource: any) => resource.ResourceId !== 'allResources');

        params.Resources = stringifyNestedParams(filteredResources);
    }

    try {
        const response: AxiosResponse = yield apiClient.get(`/services/${ServiceId}/availabletimes`, {
            params,
        });

        yield put(actions.FETCH_AVAILABLE_TIMES.success({ Results: response.data.Times }));

        if (response.data.Times.length === 0) {
            yield put(
                actions.FETCH_NEXT_FREE_TIME.request({
                    ServiceId,
                    ...params,
                    To: format(addYears(From, 1), DATE_FORMAT),
                })
            );
        }
    } catch (error) {
        yield put(actions.FETCH_AVAILABLE_TIMES.failure(error));
    }
}

export function* fetchNextFreeTime({ payload: { ServiceId, Resources, ...params } }: AnyAction) {
    try {
        const response: AxiosResponse = yield apiClient.get(`/services/${ServiceId}/nextfreetime`, {
            params: {
                ...params,
                Resources: stringifyNestedParams(Resources),
            },
        });

        yield put(actions.FETCH_NEXT_FREE_TIME.success(response.data));
    } catch (error) {
        yield put(actions.FETCH_NEXT_FREE_TIME.failure(error));
    }
}

/** One interval being submitted — a single slot, or one generated repeat date. */
export type Occurrence = { From: Date | string; To: Date | string };

/** A queue item as the API returns it (`/bookinguserqueue` POST 200 schema). */
type QueueItem = definitions['BookingUserQueueItemResponse'];

/**
 * The booking form's `Resources` map (`{ _1234: [id, …] }`) flattened into the
 * `{ ResourceTypeId, ResourceId }` pairs both `/bookings/repeat` and
 * `/services/{id}/availabletimes` expect. One shape, two callers: availability
 * has to be looked up for the same resources the booking will be made against.
 */
export const toResourceSelections = (Resources: { [key: string]: number[] } | undefined) =>
    Resources
        ? Object.keys(Resources)
            .reduce(
                (acc: any[], key: string) => [
                    ...acc,
                    ...Resources[key].map((ResourceId: number) => ({
                        // _1234 => 1234
                        ResourceTypeId: key.slice(1),
                        ResourceId,
                    })),
                ],
                []
            )
            .filter((obj) => !!obj.ResourceId)
        : undefined;

/**
 * Why a queue request failed, in the same shape `/bookings/repeat` uses for its
 * own per-date failures, so one list can carry both kinds.
 *
 * 409 is not a generic error: it means this customer is already waiting for this
 * exact interval, or the interval's queue has hit its 50-entry cap. It is flagged
 * because there is nothing to retry — the entry the admin wanted already exists.
 */
export const toQueueFailure = (occurrence: Occurrence, error: any) => {
    const data = error?.response?.data;

    return {
        From: occurrence.From,
        To: occurrence.To,
        Quantities: [],
        Reason:
            data?.ResponseStatus?.Message ||
            data?.ResponseStatus?.Errors?.[0]?.Message ||
            error?.message ||
            '',
        IsQueue: true,
        IsDuplicate: error?.response?.status === 409,
    };
};

/**
 * Real availability for every occurrence being submitted, in ONE request.
 *
 * The calendar only ever fetched the window it was showing, so a recurring
 * series can reach weeks past it — and a generated occurrence carries the seed
 * slot's `Free`/`FreeSpots` verbatim (`getRepeatedDates` spreads the seed), so
 * the occurrence object cannot answer for itself.
 *
 * Read straight through `apiClient` rather than dispatching
 * FETCH_AVAILABLE_TIMES: that action replaces the calendar's own window in the
 * store and, on an empty result, starts a next-free-time search. Neither belongs
 * in a submit.
 *
 * On failure the known lookup comes back unchanged, which classifies the
 * unanswered occurrences as bookable and lets the server decide. A lookup that
 * did not resolve must never put a customer on a waiting list.
 */
export function* fetchFreeSpotsForOccurrences(
    known: Map<number, number>,
    occurrences: Occurrence[],
    request: { ServiceId?: number; Resources?: any[]; Duration?: number }
): Generator<any, Map<number, number>, any> {
    const startInstants = occurrences
        .map((occurrence) => toInstantKey(occurrence.From))
        .filter((instant): instant is number => instant !== undefined);

    if (!request.ServiceId || startInstants.length === 0) return known;

    const endInstants = occurrences
        .map((occurrence) => toInstantKey(occurrence.To))
        .filter((instant): instant is number => instant !== undefined);

    const params: any = {
        From: format(new Date(Math.min(...startInstants)), DATE_FORMAT),
        To: format(
            new Date(Math.max(...(endInstants.length > 0 ? endInstants : startInstants))),
            DATE_FORMAT
        ),
        ...(request.Duration ? { Duration: request.Duration } : {}),
        ...(request.Resources && request.Resources.length > 0
            ? { Resources: stringifyNestedParams(request.Resources) }
            : {}),
    };

    try {
        const response: AxiosResponse = yield apiClient.get(
            `/services/${request.ServiceId}/availabletimes`,
            { params }
        );

        // The freshly fetched answer wins over the store's, which may predate a
        // booking made elsewhere in the meantime.
        const merged = new Map<number, number>();
        known.forEach((free, key) => merged.set(key, free));
        buildFreeSpotsByFrom(response?.data?.Times).forEach((free, key) => merged.set(key, free));

        return merged;
    } catch (error) {
        return known;
    }
}

export function* createBooking({ payload }: AnyAction) {
    const action = actions.CREATE_BOOKING;

    const bookingForm: { [key in string]: any } = yield select(getFormValues('booking'));
    const quantities: { [key in string]: any } = yield select((state) => state.booking.quantities);
    const finalService: { [key in string]: any } = yield select((state) => state.booking.finalService.data);
    const sliceQuantities: Record<string, Record<number, number>> = yield select(selectAllQuantities);
    const location: RootState['router']['location'] = yield select((state) => state.router.location);
    const time: Time = yield select((state: ApplicationState) => state.booking.time);



    const {
        customerInputMode,
        ServiceId,
        PaymentOption,
        BookedComments,
        CommentsToCustomer,
        CustomFields,
        SendSmsConfirmation,
        SendEmailReminder,
        SendEmailConfirmation,
        SendSmsReminder,
        Resources,
        Customer,
        DatesToRepeat,
        repeat,
        TagIds,
        Articles,
        _articleDurationMinutes,
    } = bookingForm;

    // Service add-ons lengthen the booking, but the backend will not do it for us:
    // VerifyServiceDurationAsync returns early for an admin, so it books exactly the
    // `To` we send and silently ignores `article.Duration × Quantity`.
    //
    // The extension is applied HERE rather than in the form because `To` is the key
    // of the interval-addressed price state: mutating it re-keys
    // state.calculatePrice.entity.calculatedPrice and the DateQuantities_ redux-form
    // instances, which orphans quoted lines and resets the admin's quantities.
    // The quote is unaffected by add-on duration anyway — calculateprice never reads
    // Duration — so the price interval stays the service interval and only the
    // posted booking is extended.
    const articleMinutes =
        Array.isArray(Articles) && Articles.length > 0
            ? toMinutes(_articleDurationMinutes)
            : 0;
    const withArticleDuration = (to: Date | string) => extendTo(to, articleMinutes) ?? to;

    const invoiceAddress = Customer.InvoiceAddress;
    const customer = omit(Customer, 'InvoiceAddress')

    const from = toDate(payload?.From);
    const to = toDate(payload?.To);
    const serviceDuration = finalService?.Duration;
    // Measured on the length actually being booked, add-ons included, so an extended
    // booking is allowed outside the schedule the same way a flexible-duration one is.
    const currentDuration = Math.abs(differenceInMinutes(from, to)) + articleMinutes;

    let AllowBookingOutsideSchedules;
    if (location.pathname === '/scheduler') {
        AllowBookingOutsideSchedules = true
    } else if (serviceDuration === currentDuration) {
        AllowBookingOutsideSchedules = false;
    } else if (serviceDuration !== currentDuration) {
        AllowBookingOutsideSchedules = true;
    }

    const getQuantitiesForDate = (fromDate: string | Date, forms?: any) => {
        const fromStr = typeof fromDate === 'string' ? fromDate : toDate(fromDate).toISOString();
        return resolveQuantitiesForDate(fromStr, forms, sliceQuantities, quantities, finalService?.Prices);
    };

    // Every interval this submit is for. The recurring selection lives in
    // `DatesToRepeat`; `state.booking.time` describes only the non-recurring case.
    // The form values are the fallback for a submit where no slot was ever
    // remembered — previously that threw on `time.From` and surfaced as a generic
    // failure.
    const occurrences: Occurrence[] = repeat
        ? (flatten(DatesToRepeat) as { From: Date; To: Date; selected: boolean }[])
            .filter((date) => date.selected)
            .map((date) => ({ From: date.From, To: date.To }))
        : [time ? { From: time.From, To: time.To } : { From: payload?.From, To: payload?.To }];

    const queueEnabled = !!finalService?.EnableBookingQueue;
    const resourceSelections = toResourceSelections(Resources);

    try {
        const forms: { [key in string]: any } = yield select((s: ApplicationState) => s.form);
        const rebateCodeIds: number[] = yield select(getAppliedCodeIds);

        // Availability is only consulted when the service HAS a queue: without one
        // every occurrence is bookable whatever its spot count, so those flows post
        // exactly the request they post today — no extra call, no new failure mode.
        let freeSpotsByFrom: Map<number, number> = new Map();

        if (queueEnabled && occurrences.length > 0) {
            freeSpotsByFrom = yield select(getFreeSpotsByFrom);

            const covered = (occurrence: Occurrence) => {
                const key = toInstantKey(occurrence.From);
                return key !== undefined && freeSpotsByFrom.has(key);
            };

            if (!occurrences.every(covered)) {
                freeSpotsByFrom = yield call(
                    fetchFreeSpotsForOccurrences,
                    freeSpotsByFrom,
                    occurrences,
                    {
                        ServiceId: finalService?.Id ?? ServiceId,
                        Resources: resourceSelections,
                        // Mirrors what the calendar asked for, so the same slot
                        // boundaries come back: a flexible-duration service is
                        // fetched at the duration the admin picked.
                        Duration:
                            finalService?.DurationTypeId !== 1
                                ? bookingForm._serviceDuration
                                : undefined,
                    }
                );
            }
        }

        const freeSpotsFor = (occurrence: Occurrence) => {
            const key = toInstantKey(occurrence.From);
            return key === undefined ? undefined : freeSpotsByFrom.get(key);
        };

        // Full AND a queue exists → the waiting queue. Everything else → the
        // ordinary endpoint, including a full occurrence on a service with no queue:
        // the server refuses it and it surfaces in `Failed`, which is the current,
        // honest behaviour. Unknown availability counts as bookable for the same
        // reason — never put a customer on a waiting list they did not ask for.
        const bookable: Occurrence[] = [];
        const queueable: Occurrence[] = [];

        occurrences.forEach((occurrence) => {
            if (queueEnabled && freeSpotsFor(occurrence) === 0) {
                queueable.push(occurrence);
            } else {
                bookable.push(occurrence);
            }
        });

        // The request-level Quantities this endpoint has always carried, taken from
        // the remembered slot. Unchanged — it is not the per-date list.
        const requestQuantitiesFrom = time
            ? typeof time.From === 'string' ? time.From : toDate(time.From).toISOString()
            : '';

        let created: any[] = [];
        let failed: any[] = [];
        let responseStatus: any;

        // Bookings first, as ONE call: it preserves the server's own batch semantics
        // and today's notification behaviour, and its CustomerId is what the queue
        // requests below reuse. Skipped only when the queue took every occurrence —
        // a submit with nothing selected still posts, exactly as it does today.
        if (bookable.length > 0 || queueable.length === 0) {
            const response: AxiosResponse = yield apiClient.post(`/bookings/repeat`, {
                DatesToRepeat: bookable.map((occurrence) => ({
                    From: occurrence.From,
                    To: withArticleDuration(occurrence.To),
                    Quantities: getQuantitiesForDate(occurrence.From, forms),
                })),
                ...(invoiceAddress ? { InvoiceAddress: invoiceAddress } : {}),
                ServiceId,
                PaymentOption,
                BookedComments,
                CommentsToCustomer,
                SendSmsConfirmation,
                SendEmailReminder,
                SendEmailConfirmation,
                SendSmsReminder,
                Resources: resourceSelections,
                Quantities: requestQuantitiesFrom
                    ? getQuantitiesForDate(requestQuantitiesFrom, forms)
                    : [],
                CustomFields,
                CustomerId: customerInputMode === 'search' ? Customer.Id : undefined,
                Customer: customer,
                CustomerCustomFields: Customer.CustomFields,
                AllowBookingOutsideSchedules,
                RebateCodeIds: rebateCodeIds,
                ...(TagIds && TagIds.length > 0 ? { TagIds } : {}),
                // Omitted entirely when the picker is untouched, so every existing
                // booking flow posts a byte-identical body. The queue requests below
                // cannot carry add-ons — `CreateBookingUserQueue` has no Articles.
                ...(Array.isArray(Articles) && Articles.length > 0 ? { Articles } : {}),
            });

            created = response?.data?.Created ?? [];
            failed = response?.data?.Failed ?? [];
            responseStatus = response?.data?.ResponseStatus;
        }

        // Carry ONE customer across every call. `GetCustomerAsync` resolves by id,
        // else dedupes by email, else creates a new customer and fires a
        // CustomerCreated webhook — so a Customer object with no id and no email
        // would create a fresh customer per request, each queue entry pointing at a
        // different person. The server does the same for repeat bookings
        // (`request.CustomerId ??= booking.CustomerId`).
        let customerId: string | undefined =
            Customer?.Id || created.find((booking: any) => booking?.CustomerId)?.CustomerId;

        const queued: QueueItem[] = [];
        const queueFailed: any[] = [];

        // Sequential, one per occurrence: there is no batch queue endpoint. Distinct
        // intervals never collide (the duplicate check is keyed on the exact
        // interval) and no notification is sent on queue creation, so N calls do not
        // mean N emails.
        for (const occurrence of queueable) {
            const queuePayload: definitions['CreateBookingUserQueue'] = {
                Customer: customer,
                ...(invoiceAddress ? { InvoiceAddress: invoiceAddress } : {}),
                CustomerId: customerId,
                ServiceId: finalService.Id,
                From: toDate(occurrence.From).toISOString(),
                To: toDate(occurrence.To).toISOString(),
                // `request.Quantities.Any()` is unguarded server-side and neither the
                // DTO nor the validator supplies a default, so a missing array is an
                // opaque 500. Always send one, even empty.
                Quantities: getQuantitiesForDate(occurrence.From, forms) ?? [],
            };

            try {
                const response: AxiosResponse<BookingUserQueueItemResponse> =
                    yield apiClient.post(`/bookinguserqueue`, queuePayload);

                // Support both legacy and current API shapes:
                // - Legacy: { BookingUserQueue: { ... }, ResponseStatus: { Message } }
                // - Current: { BookingUserQueueId, CompanyId, ... } (queue item directly)
                const queueItem = (response.data as any)?.BookingUserQueue ?? response.data;

                queued.push(queueItem);

                if (!customerId && queueItem?.CustomerId) {
                    customerId = queueItem.CustomerId;
                }
            } catch (error) {
                // One failure must not abort the rest: the occurrences are
                // independent, and a 409 on one says nothing about the others.
                queueFailed.push(toQueueFailure(occurrence, error));
            }
        }

        const allFailed = [...failed, ...queueFailed];

        if (created.length > 0 || queued.length > 0) {
            const successPayload: definitions['CreateBookingsResponse'] & {
                Queued: QueueItem[];
                addedToQueue?: QueueItem;
            } = {
                Created: created,
                Failed: allFailed,
                Queued: queued,
                // The redirect straight to the queue tab belongs to the case it was
                // built for: one queue entry and nothing booked. A mixed submit stays
                // on the result screen, which reports every group.
                ...(queued.length === 1 && created.length === 0
                    ? { addedToQueue: queued[0] }
                    : {}),
            };

            yield put(action.success(successPayload));
        } else if (allFailed.length > 0) {
            const errorMessage = responseStatus?.Message ? `${responseStatus.Message}\n` : '';
            const errors = allFailed.map((failure: any) => `• ${format(failure.From, 'yyyy-MM-dd p')} - ${format(failure.To, 'yyyy-MM-dd p')} ${failure.Reason}`).join('\n')

            yield put(
                actions.CREATE_BOOKING.failure(
                    throwSubmissionError({
                        _error: `${errorMessage}${errors}`,
                    })
                )
            );
        }
    } catch (error) {
        yield put(actions.CREATE_BOOKING.failure(throwSubmissionError(error)));
    }
}


export function* updateServiceQuantity({ payload }: AnyAction) {
    try {
        const { service, selectedDates, selectedPriceIds } = payload || {};
        const quantities = getServiceQuantities(service, selectedPriceIds);

        const responses: any[] = yield all(
            quantities.map((quantity, index) => apiClient.put(`/services/${service.Id}/calculateprice`, {
                Id: service.Id,
                Quantities: quantity,
                Interval: selectedDates[index]
            }))
        );

        yield put(actions.UPDATE_SERVICE_QUANTITY.success(responses.map(r => r.data)));
    } catch (error) {
        yield put(actions.UPDATE_SERVICE_QUANTITY.failure(error))
    }



}
